// Review links. ReviewApp is the page someone opens from /review#<token> (no account); it never uses the
// signed-in API. ReviewLinksSection is Blue Market's list of links on a project page.
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, patch, post, put } from '../api';
import { LiveProvider, useLiveTopics } from '../live';
import { CopyField, Menu, Modal, ago, formatDate, useConfirm, useToast } from '../ui';
import type { Project, ReviewLink, ReviewLinkState, ReviewSettings, ReviewerState } from '../types';
import { RoundView } from './Designs';
import { useComments, type CommentsApi } from './Comments';
import { ClientQuestionsList, REVIEW_QUESTIONS, useClientQuestions } from './Questions';

const Logo = () => <img className="logo" src="/assets-static/logo.png" alt="Blue Market" />;

const REVIEW_COMMENTS: CommentsApi = {
  key: ['review-comments'],
  list: '/rapi/comments',
  item: (id) => `/rapi/comments/${id}`,
  refresh: [['review-state']],
  upload: '/rapi/attachments',
};

// ================= the page link holders see =================

type Step = { kind: 'opening' } | { kind: 'name'; token: string; project: string; client: string } | { kind: 'in' } | { kind: 'error'; message: string };

export function ReviewApp() {
  const [step, setStep] = useState<Step>({ kind: 'opening' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = decodeURIComponent(location.hash.slice(1));
    // The token never stays in the address bar (or in history, bookmarks and screenshots).
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    if (!token) { setStep({ kind: 'in' }); return; }
    post<{ ok?: boolean; needs_name?: boolean; project?: string; client?: string }>('/rapi/session', { token })
      .then((r) => setStep(r.needs_name ? { kind: 'name', token, project: r.project ?? '', client: r.client ?? '' } : { kind: 'in' }))
      .catch((e: Error) => setStep({ kind: 'error', message: e.message }));
  }, []);

  if (step.kind === 'opening') return <div className="auth-wrap"><span className="spin muted" /></div>;
  if (step.kind === 'error') return <Gone message={step.message} />;
  if (step.kind === 'name') return <ChooseName {...step} onDone={() => setStep({ kind: 'in' })} />;
  return <LiveProvider enabled><Reviewing /></LiveProvider>;
}

function Gone({ message }: { message: string }) {
  return (
    <div className="auth-wrap">
      <div className="card auth-card stack">
        <Logo />
        <div><div className="eyebrow">Blue Market</div><h1>Design review</h1></div>
        <p className="muted" style={{ margin: 0 }}>{message}</p>
      </div>
    </div>
  );
}

function ChooseName({ token, project, client, onDone }: { token: string; project: string; client: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { await post('/rapi/session', { token, name }); onDone(); } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Logo />
        <div><div className="eyebrow">{client}</div><h1>{project}</h1></div>
        <p className="muted small" style={{ margin: 0 }}>Blue Market has shared designs with you for comment. What's your name? It's shown next to your comments, and this link stays yours from now on.</p>
        <label className="field"><span>Your name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="name" required autoFocus /></label>
        {error && <div className="error">{error}</div>}
        <button className="btn primary block" disabled={busy || !name.trim()}>{busy && <span className="spin" />}Continue</button>
      </form>
    </div>
  );
}

function Reviewing() {
  const { data: state, error, isLoading } = useQuery({ queryKey: ['review-state'], queryFn: () => get<ReviewerState>('/rapi/state'), retry: false });
  const { data: comments } = useComments(REVIEW_COMMENTS, Boolean(state?.round));
  const { data: questions } = useClientQuestions(REVIEW_QUESTIONS, Boolean(state?.questions));
  const [tab, setTab] = useState<'designs' | 'questions' | null>(null);
  useLiveTopics(state ? `review:${state.project_id}` : null);
  useEffect(() => { if (state) document.title = `${state.project} · Design review`; }, [state]);

  if (error) return <Gone message={error instanceof ApiError && error.status === 401 ? error.message : 'Something went wrong. Try again in a moment.'} />;
  if (isLoading || !state) return <div className="auth-wrap"><span className="spin muted" /></div>;
  const hasQuestions = !!state.questions?.total;
  // Open questions waiting, or nothing to review: start on the questions.
  const shown = tab ?? (hasQuestions && (!state.round || (state.questions?.open ?? 0) > 0) ? 'questions' : 'designs');

  return (
    <div className="review-shell">
      <header className="review-head">
        <img src="/assets-static/logo.png" alt="Blue Market" className="review-logo" />
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="ellipsis"><b>{state.project}</b> <span className="muted review-client">· {state.client}</span></div>
        </div>
        {hasQuestions && (
          <nav className="review-tabs" aria-label="Sections">
            <button className={shown === 'designs' ? 'active' : ''} onClick={() => setTab('designs')} aria-pressed={shown === 'designs'}>Designs</button>
            <button className={shown === 'questions' ? 'active' : ''} onClick={() => setTab('questions')} aria-pressed={shown === 'questions'}>
              Questions{!!state.questions?.open && <span className="count">{state.questions.open}</span>}
            </button>
          </nav>
        )}
        <span className="muted small ellipsis review-name" title={`This link expires on ${formatDate(state.expires_at)}`}>{state.name}</span>
      </header>
      {shown === 'questions' && hasQuestions ? (
        <main className="page" style={{ maxWidth: 820 }}>
          <div className="page-head"><div><div className="eyebrow">{state.client}</div><h1>Questions from Blue Market</h1></div></div>
          <p className="muted small" style={{ margin: '0 0 14px' }}>We need these answered to finish the designs and plan the build. Your answers go straight to the team, and you can change them later.</p>
          {questions ? <ClientQuestionsList api={REVIEW_QUESTIONS} data={questions} /> : <span className="spin muted" />}
        </main>
      ) : state.round ? (
        <RoundView round={state.round} comments={comments} commentsApi={REVIEW_COMMENTS} eyebrow={`${state.project} · v${state.round.number}`}
                   screenUrl={(sid) => `/rfile/screens/${encodeURIComponent(sid)}.html`} pdfUrl="/rfile/pdf"
                   headLeft={<b>Designs v{state.round.number}</b>} />
      ) : (
        <main className="page"><div className="card empty">There's nothing to review yet. Blue Market will let you know when designs are ready.</div></main>
      )}
    </div>
  );
}

// ================= Blue Market: links on a project page =================

const STATE_LABEL: Record<ReviewLinkState, string> = { unused: 'Not opened yet', claimed: 'In use', expired: 'Expired', revoked: 'Revoked' };
const STATE_BADGE: Record<ReviewLinkState, string> = { unused: 'warn', claimed: 'ok', expired: '', revoked: '' };

export function ReviewLinksSection({ project }: { project: Project }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const { data } = useQuery({ queryKey: ['review', project.id], queryFn: () => get<ReviewSettings>(`/api/projects/${project.id}/review`) });
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ label: string; url: string } | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['review', project.id] });
  const onError = (e: Error) => toast(e.message, true);

  const setRound = useMutation({ mutationFn: (round: number | null) => put(`/api/projects/${project.id}/review/round`, { round }), onSuccess: refresh, onError });
  const reissue = useMutation({
    mutationFn: (l: ReviewLink) => post<{ link: ReviewLink; url: string }>(`/api/review-links/${l.id}/reissue`),
    onSuccess: (r) => { refresh(); setIssued({ label: r.link.label, url: r.url }); }, onError,
  });
  const revoke = useMutation({ mutationFn: (l: ReviewLink) => post(`/api/review-links/${l.id}/revoke`), onSuccess: refresh, onError });
  const toggleQuestions = useMutation({ mutationFn: (l: ReviewLink) => patch(`/api/review-links/${l.id}`, { answers_questions: !l.answers_questions }), onSuccess: refresh, onError });

  const { active, inactive } = useMemo(() => {
    const links = data?.links ?? [];
    return { active: links.filter((l) => l.state === 'unused' || l.state === 'claimed'), inactive: links.filter((l) => l.state === 'expired' || l.state === 'revoked') };
  }, [data]);
  if (!data) return null;
  const shown = showInactive ? [...active, ...inactive] : active;
  const roundOptions = [...new Set([...data.shareable_rounds, ...(data.review_round ? [data.review_round] : [])])].sort((a, b) => b - a);

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>Review links</h2>
        <button className="btn sm" onClick={() => setCreating(true)}>New link</button>
      </div>
      <div className="stack" style={{ gap: 6, padding: '12px 18px', borderBottom: '1px solid var(--line)' }}>
        <label className="row wrap" style={{ gap: 8 }}>
          <span className="small">Round shown to links</span>
          <select className="input" style={{ width: 'auto', minHeight: 30, padding: '3px 8px' }} value={data.review_round ?? ''}
                  onChange={(e) => setRound.mutate(e.target.value ? Number(e.target.value) : null)} aria-label="Round shown to review links">
            <option value="">None</option>
            {roundOptions.map((n) => <option key={n} value={n}>v{n}</option>)}
          </select>
        </label>
        <p className="muted tiny" style={{ margin: 0 }}>
          For people without an account. Each link is for one person: they choose their name the first time, then it opens straight in on any device.
          A link only shows a round that's ready for comment or approved. Links also show the questions you ask the client, unless you switch that off for a link.
        </p>
      </div>
      {shown.map((l) => (
        <div key={l.id} className="round-row">
          <div className="grow" style={{ minWidth: 180 }}>
            <div className="row wrap" style={{ gap: 6 }}>
              <b className="small">{l.label}</b>
              <span className={`badge ${STATE_BADGE[l.state]}`}>{STATE_LABEL[l.state]}</span>
              {l.round_pin && <span className="badge" title="Stays on this round when the project's review round changes">Pinned to v{l.round_pin}</span>}
              {l.comment_count > 0 && <span className="badge brand">{l.comment_count} comment{l.comment_count === 1 ? '' : 's'}</span>}
              {!l.answers_questions && <span className="badge" title="This link can't see or answer the questions you ask the client">No questions</span>}
            </div>
            <div className="muted tiny" style={{ marginTop: 4 }}>
              {l.reviewer_name ? `Opened as ${l.reviewer_name}` : 'Not opened yet'}
              {l.last_seen_at && ` · last seen ${ago(l.last_seen_at)}`}
              {l.state === 'revoked' ? ` · revoked ${ago(l.revoked_at)}` : l.state === 'expired' ? ` · expired ${formatDate(l.expires_at)}` : ` · expires ${formatDate(l.expires_at)}`}
              {l.created_by && ` · created by ${l.created_by}`}
            </div>
          </div>
          <Menu label={`Actions for ${l.label}`} items={[
            { label: l.answers_questions ? "Don't let them answer questions" : 'Let them answer questions', onClick: () => toggleQuestions.mutate(l) },
            { label: l.state === 'revoked' || l.state === 'expired' ? 'Reactivate with a new link' : 'New link', onClick: async () => {
              if (await confirm(`New link for ${l.label}?`, { body: 'The current link, and every device using it, stops working. Their name and comments stay.', ok: 'Make a new link' })) reissue.mutate(l);
            } },
            ...(l.state !== 'revoked' ? [{ label: 'Revoke', danger: true, onClick: async () => {
              if (await confirm(`Revoke ${l.label}'s link?`, { body: 'It stops working straight away, on every device. Their comments stay.', ok: 'Revoke', danger: true })) revoke.mutate(l);
            } }] : []),
          ]} />
        </div>
      ))}
      {inactive.length > 0 && !showInactive && (
        <button className="btn ghost sm block" style={{ borderRadius: 0, height: 40 }} onClick={() => setShowInactive(true)}>Show {inactive.length} expired or revoked</button>
      )}
      {!data.links.length && <div className="empty">No review links yet.</div>}
      {creating && <NewLinkDialog project={project} rounds={data.shareable_rounds} onClose={() => setCreating(false)}
                                  onCreated={(label, url) => { setCreating(false); setIssued({ label, url }); refresh(); }} />}
      {issued && (
        <Modal title={`Link for ${issued.label}`} onClose={() => setIssued(null)}>
          <div className="stack">
            <p className="small" style={{ margin: 0 }}>Copy it now and send it to them: it's only shown once. Anyone with this link can open it until they choose a name, so send it to that person only.</p>
            <CopyField value={issued.url} />
            <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn primary" onClick={() => setIssued(null)}>Done</button></div>
          </div>
        </Modal>
      )}
      {dialog}
    </section>
  );
}

function NewLinkDialog({ project, rounds, onClose, onCreated }: { project: Project; rounds: number[]; onClose: () => void; onCreated: (label: string, url: string) => void }) {
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [pin, setPin] = useState('');
  const [days, setDays] = useState(30);
  const [answers, setAnswers] = useState(true);
  const create = useMutation({
    mutationFn: () => post<{ link: ReviewLink; url: string }>(`/api/projects/${project.id}/review/links`, { label, round_pin: pin ? Number(pin) : null, days, answers_questions: answers }),
    onSuccess: (r) => onCreated(r.link.label, r.url),
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title="New review link" onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <label className="field"><span>Who it's for</span>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="e.g. Marketing lead at the client" required autoFocus />
          <span className="hint">Only Blue Market sees this. They choose the name shown on their comments.</span>
        </label>
        <label className="field"><span>Round</span>
          <select className="input" value={pin} onChange={(e) => setPin(e.target.value)}>
            <option value="">Follow the project's review round</option>
            {rounds.map((n) => <option key={n} value={n}>Always v{n}</option>)}
          </select>
        </label>
        <label className="field"><span>Expires after</span>
          <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
        </label>
        <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={answers} onChange={(e) => setAnswers(e.target.checked)} style={{ marginTop: 3 }} />
          <span className="small">They can answer the questions you ask the client<br /><span className="muted tiny">Only questions marked "Ask the client" on the Open questions page.</span></span>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!label.trim() || create.isPending}>{create.isPending && <span className="spin" />}Create link</button>
        </div>
      </form>
    </Modal>
  );
}
